import { ref, watch } from 'vue'

class Prompter {
    /**
    * prompt state
    * 
    * @private
    */
    #state = ref({
        clicked: '',
    })


    /**
     * prompt visible state
     * 
     * @type {ref<boolean>}
     */
    visible = ref(false)


    /**
     * details of the prompt
     * 
     * @type {{title: string, message: string, icon: string, iconColor: string, iconBgColor: string, ok: string, cancel: string}}
     */
    details = ref({
        title: 'Unsaved changes',
        message: 'Do you want to save or dismiss your changes?',
        icon: 'alertTriangle',
        iconColor: '#D06B0F',
        iconBgColor: '#FEEFC6',
        ok: 'Save',
        cancel: 'Dismiss',
    })


    /**
     * prevent default
     * 
     * @private
     */
    #preventDefault = () => {
        return event.preventDefault()
    }


    /**
     * disable default scroll
     * 
     * @private
     */
    #disableScroll = () => {
        document.body.style.overflow = 'hidden'
        document.addEventListener('touchmove', this.#preventDefault(), { passive: false })
        document.addEventListener('wheel', this.#preventDefault(), { passive: false })
    }


    /**
     * enable default scroll
     * 
     * @private
     */
    #enableScroll = () => {
        document.body.style.overflow = ''
        document.removeEventListener('touchmove', this.#preventDefault())
        document.removeEventListener('wheel', this.#preventDefault())
    }


    /**
     * shows the prompt.
     * 
     * @private
     */
    #show = () => {
        this.#state.value.clicked = '' // reset state clicked value

        this.visible.value = true // visible prompt modal
        this.#disableScroll() // disable scroll
    }


    /**
     * closes the prompt.
     * 
     */
    close = (type) => {
        this.visible.value = false // hide prompt modal
        this.#enableScroll() // enable scroll

        if (type) {
            this.#state.value.clicked = type // set state clicked value
        }
    }


    /**
     * run the prompt
     * 
     * @param {object} info
     * @param {string} info.title - The title of the prompt box.
     * @param {string} info.message - The message displayed in the prompt box.
     * @param {string} info.icon - The icon for the prompt box.
     * @param {string} info.iconColor - The color of the icon.
     * @param {string} info.iconBgColor - The background color of the icon.
     * @param {string} info.ok - The text for the 'OK' action.
     * @param {string} info.cancel - The text for the 'Cancel' action.
     */
    fire = (info) => {
        return new Promise((resolve) => {
            Object.entries(info).forEach(([key, value]) => this.details.value[key] = value) // override details default value 

            this.#show() // show prompt

            // watch for clicked state & resolve
            watch(() => this.#state.value.clicked, (value) => {
                resolve({ [value]: true })
            })
        })
    }

    /**
     * Ask a dangerous question.
     *
     * @param {string} message - The message displayed in the prompt box.
     * @param {object} options - The options for the prompt box.
     * @return {Promise}
     */
    ask = (message, options = {}) => {
        return this.fire({
            message,
            ok: 'Delete',
            cancel: 'Cancel',
            icon: 'alertTriangle',
            iconColor: '#D06B0F',
            iconBgColor: '#FEEFC6',
            title: 'Are you sure?',
            ...options,
        })
    }
}


const Prompt = new Prompter()

export default Prompt